import { Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Building2,
  ClipboardEdit,
  ExternalLink,
  Loader2,
  Mail,
  MapPin,
  Navigation,
} from "lucide-react";
import { FaFacebook, FaInstagram, FaLinkedin } from "react-icons/fa6";
import { useBusinessById } from "../hooks/useBusinessById";
import { useWellKnown } from "../hooks/useWellKnown";
import "../styles/components/action-button.css";
import "../styles/pages/business-details.css";

export default function BusinessDetails() {
  const { id } = useParams({ from: "/business/$id" });
  const { data: business, isLoading, isError } = useBusinessById(id);
  const { data: wellKnown } = useWellKnown();

  if (isLoading) {
    return (
      <div className="container page-content">
        <div className="business-status">
          <Loader2 size={32} className="spin" />
          <p>Loading business details…</p>
        </div>
      </div>
    );
  }

  if (isError || !business) {
    return (
      <div className="container page-content">
        <div className="business-status business-status--error">
          <p>Business not found or failed to load.</p>
          <Link
            to="/"
            className="action-button action-button--primary"
            style={{ flex: "none" }}
          >
            Back to Directory
          </Link>
        </div>
      </div>
    );
  }

  const primary = business.primary_color ?? "var(--color-primary)";
  const secondary = business.secondary_color ?? "var(--color-secondary)";

  // Australian address format: unit / house number street, suburb state postcode
  const addressLine1 = [
    business.unit_number && `Unit ${business.unit_number}`,
    business.house_number,
    business.street,
  ]
    .filter(Boolean)
    .join(" ");
  const addressLine2 = [business.city, business.region, business.zip]
    .filter(Boolean)
    .join(" ");
  const addressFull = [addressLine1, addressLine2, business.country]
    .filter(Boolean)
    .join(", ");

  // Parse coords safely — API may return strings or null
  const lat = parseFloat(String(business.latitude ?? ""));
  const lon = parseFloat(String(business.longitude ?? ""));
  const hasCoords = !isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0);

  const directionsUrl = `https://maps.google.com/?q=${encodeURIComponent(addressFull)}`;

  const osmEmbedUrl = hasCoords
    ? `https://www.openstreetmap.org/export/embed.html` +
      `?bbox=${lon - 0.02},${lat - 0.02},${lon + 0.02},${lat + 0.02}` +
      `&layer=mapnik&marker=${lat},${lon}`
    : null;

  const hasSocial =
    business.facebook_page ||
    business.instagram_page ||
    business.linked_in_page;

  return (
    <div
      className="business-details"
      style={
        {
          "--card-primary": primary,
          "--card-secondary": secondary,
        } as React.CSSProperties
      }
    >
      <div className="container">
        <Link to="/" className="business-details__back-link">
          <ArrowLeft size={16} />
          Back to Directory
        </Link>

        {/* ── Hero card ── */}
        <div className="business-details__hero-card">
          <div className="business-details__hero-accent-bar" />
          <div className="business-details__hero-body">
            <div className="business-details__hero-logo-wrapper">
              {business.business_image ? (
                <img
                  className="business-details__logo-image"
                  src={business.business_image}
                  alt={`${business.business_name} logo`}
                />
              ) : (
                <div className="business-details__logo-placeholder">
                  <Building2 size={40} />
                </div>
              )}
            </div>

            <div className="business-details__hero-text">
              <h1 className="business-details__name">{business.business_name}</h1>
              {business.profile_summary && (
                <p className="business-details__summary">
                  {business.profile_summary}
                </p>
              )}
              {business.tags?.filter((tag) => tag?.trim()).length > 0 && (
                <div className="business-details__tags">
                  {business.tags.filter((tag) => tag?.trim()).map((tag) => (
                    <span key={tag} className="business-details__tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="business-details__hero-actions">
              {business.website && (
                <a
                  className="action-button action-button--primary"
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={14} />
                  Visit Site
                </a>
              )}
              {business.contact_email && (
                <a
                  className="action-button action-button--outline"
                  href={`mailto:${business.contact_email}`}
                >
                  <Mail size={14} />
                  Send Email
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ── Two-column layout ── */}
        <div className="business-details__layout">
          {/* Main column — HTML overview + map */}
          <div className="business-details__main-column">
            {business.business_overview && (
              <div className="detail-card">
                <h2 className="detail-card__title">About This Business</h2>
                <div
                  className="detail-card__overview-content"
                  dangerouslySetInnerHTML={{
                    __html: business.business_overview,
                  }}
                />
              </div>
            )}

            {osmEmbedUrl && (
              <div className="detail-card detail-card--map">
                <div className="detail-card__map-header">
                  <div className="detail-card__map-header-left">
                    <h2 className="detail-card__title">Location</h2>
                    {addressFull && (
                      <p className="detail-card__map-address">
                        <MapPin size={13} />
                        {addressFull}
                      </p>
                    )}
                  </div>
                  <a
                    className="action-button action-button--primary detail-card__directions-button"
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Navigation size={14} />
                    Get Directions
                  </a>
                </div>
                <iframe
                  className="detail-card__map-frame"
                  src={osmEmbedUrl}
                  title={`Map showing location of ${business.business_name}`}
                  loading="lazy"
                  allowFullScreen
                />
              </div>
            )}
          </div>

          {/* Sidebar — info cards */}
          <div className="business-details__sidebar">
            {/* Contact & Location */}
            <div className="detail-card">
              <h3 className="detail-card__title">Contact & Location</h3>

              {business.contact_email && (
                <div className="detail-card__row">
                  <Mail size={15} />
                  <a href={`mailto:${business.contact_email}`}>
                    {business.contact_email}
                  </a>
                </div>
              )}

              {business.website && (
                <div className="detail-card__row">
                  <ExternalLink size={15} />
                  <a
                    href={business.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {business.website.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              )}

              {addressFull && (
                <div className="detail-card__row detail-card__row--address">
                  <MapPin size={15} />
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {addressLine1 && <span>{addressLine1}</span>}
                    {addressLine2 && <span>{addressLine2}</span>}
                  </a>
                </div>
              )}
            </div>

            {/* Social Media */}
            {hasSocial && (
              <div className="detail-card">
                <h3 className="detail-card__title">Social Media</h3>

                {business.facebook_page && (
                  <div className="detail-card__row">
                    <FaFacebook size={15} />
                    <a
                      href={business.facebook_page}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Facebook
                    </a>
                  </div>
                )}

                {business.instagram_page && (
                  <div className="detail-card__row">
                    <FaInstagram size={15} />
                    <a
                      href={business.instagram_page}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Instagram
                    </a>
                  </div>
                )}

                {business.linked_in_page && (
                  <div className="detail-card__row">
                    <FaLinkedin size={15} />
                    <a
                      href={business.linked_in_page}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      LinkedIn
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Business profile */}
            {(business.abn ||
              business.procurement_readiness ||
              business.open_to_connections != null ||
              business.open_to_opportunities != null) && (
              <div className="detail-card">
                <h3 className="detail-card__title">Business Profile</h3>

                {business.abn && (
                  <div className="detail-card__info-row">
                    <span className="detail-card__label">ABN</span>
                    <span>{business.abn}</span>
                  </div>
                )}

                {business.procurement_readiness && (
                  <div className="detail-card__info-row">
                    <span className="detail-card__label">Procurement</span>
                    <span
                      className={`detail-card__badge ${
                        business.procurement_readiness === "ready"
                          ? "detail-card__badge--green"
                          : "detail-card__badge--amber"
                      }`}
                    >
                      {business.procurement_readiness}
                    </span>
                  </div>
                )}

                {business.open_to_connections != null && (
                  <div className="detail-card__info-row">
                    <span className="detail-card__label">
                      Open to Connections
                    </span>
                    <span
                      className={`detail-card__badge ${
                        business.open_to_connections
                          ? "detail-card__badge--green"
                          : "detail-card__badge--grey"
                      }`}
                    >
                      {business.open_to_connections ? "Yes" : "No"}
                    </span>
                  </div>
                )}

                {business.open_to_opportunities != null && (
                  <div className="detail-card__info-row">
                    <span className="detail-card__label">
                      Open to Opportunities
                    </span>
                    <span
                      className={`detail-card__badge ${
                        business.open_to_opportunities
                          ? "detail-card__badge--green"
                          : "detail-card__badge--grey"
                      }`}
                    >
                      {business.open_to_opportunities ? "Yes" : "No"}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {wellKnown?.profile_update_request_form && (
          <div className="business-details__update-cta">
            <div className="business-details__update-cta-text">
              <span className="business-details__update-cta-heading">
                See something wrong?
              </span>
              <span className="business-details__update-cta-sub">
                Request to update your details.
              </span>
            </div>
            <a
              className="action-button action-button--outline"
              href={wellKnown.profile_update_request_form}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ClipboardEdit size={14} />
              Request Update
            </a>
          </div>
        )}

        <Link to="/" className="business-details__back-link" style={{ marginTop: "1.5rem" }}>
          <ArrowLeft size={16} />
          Back to Directory
        </Link>
      </div>
    </div>
  );
}
